import { api } from "./api";
import type { Account, BuyerRow, ConversationStatus, PatchConversationBody, Template } from "./types";

/**
 * Minimal pub/sub store for Phase 1.0.B.
 *
 * Slices:
 *   - accounts            : all OfferUp accounts (for tab strip)
 *   - buyers              : flat list with joined latest-conversation fields
 *   - selectedAccountId   : 'all' or an account id; cubes filter against it
 *   - editingConversationId : non-null when the EditorSheet is open
 *
 * Phase 2 replaces the polling refresh with SSE; the public surface
 * here (subscribe / state) won't change.
 */

type Listener = () => void;

export interface PipelineFilters {
  search:     string;
  statuses:   ConversationStatus[];   // empty = all
  unreadOnly: boolean;
}

export interface BulkStatus {
  sending: boolean;
  sent:    number;
  failed:  number;
  total:   number;
}

export interface AppState {
  accounts:               Account[];
  buyers:                 BuyerRow[];
  templates:              Template[];
  loading:                boolean;
  error:                  string | null;
  loadedAt:               number | null;
  selectedAccountId:      "all" | string;
  editingConversationId:  string | null;
  filters:                PipelineFilters;
  selectedConvIds:        Set<string>;
  bulkStatus:             BulkStatus | null;
  /** Mobile preview right rail collapse state. Persisted to
   *  localStorage so reload remembers the operator's preference. */
  previewCollapsed:       boolean;
  /** Notification mute state (FEST-PHASE-2.V). Killed audio +
   *  vibration + OS native banner; visual signals (title/favicon)
   *  still update. */
  notificationsMuted:     boolean;
  /** True until the operator dismisses or accepts the soft
   *  "Enable notifications" prompt at the top of the dashboard. */
  notificationPromptDismissed: boolean;
  /** Mirror of Notification.permission so the header re-renders
   *  when the operator grants/denies. */
  notificationPermission: "default" | "granted" | "denied" | "unsupported";
}

const PREVIEW_COLLAPSED_KEY      = "festival-ops.preview.collapsed";
const NOTIF_MUTED_KEY            = "festival-ops.notifications.muted";
const NOTIF_PROMPT_DISMISSED_KEY = "festival-ops.notifications.prompt-dismissed";
function loadPreviewCollapsed(): boolean {
  try { return localStorage.getItem(PREVIEW_COLLAPSED_KEY) === "1"; }
  catch { return false; }
}
function loadNotifMuted(): boolean {
  try { return localStorage.getItem(NOTIF_MUTED_KEY) === "1"; }
  catch { return false; }
}
function loadNotifPromptDismissed(): boolean {
  try { return localStorage.getItem(NOTIF_PROMPT_DISMISSED_KEY) === "1"; }
  catch { return false; }
}
function detectNotifPermission(): "default" | "granted" | "denied" | "unsupported" {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

const FILTERS_STORAGE_KEY = "festival-ops.filters.v1";

function loadFilters(): PipelineFilters {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return { search: "", statuses: [], unreadOnly: false };
    const parsed = JSON.parse(raw) as Partial<PipelineFilters>;
    return {
      search:     typeof parsed.search === "string" ? parsed.search : "",
      statuses:   Array.isArray(parsed.statuses) ? parsed.statuses : [],
      unreadOnly: parsed.unreadOnly === true,
    };
  } catch {
    return { search: "", statuses: [], unreadOnly: false };
  }
}

function saveFilters(f: PipelineFilters): void {
  try { localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(f)); } catch {}
}

const REFRESH_MS = 15_000;

class Store {
  state: AppState = {
    accounts:              [],
    buyers:                [],
    templates:             [],
    loading:               true,
    error:                 null,
    loadedAt:              null,
    selectedAccountId:     "all",
    editingConversationId: null,
    filters:               loadFilters(),
    selectedConvIds:       new Set(),
    bulkStatus:            null,
    previewCollapsed:      loadPreviewCollapsed(),
    notificationsMuted:    loadNotifMuted(),
    notificationPromptDismissed: loadNotifPromptDismissed(),
    notificationPermission: detectNotifPermission(),
  };
  private listeners = new Set<Listener>();
  private timer: ReturnType<typeof setInterval> | null = null;

  private eventSource: EventSource | null = null;

  start(): void {
    void this.refresh();
    // 15s poll stays as a fallback — if the SSE drops or the DO has
    // a hiccup, the poll catches us up the slow way. SSE is the
    // primary refresh trigger when connected.
    this.timer ??= setInterval(() => void this.refresh(), REFRESH_MS);
    this.openStream();
  }
  stop(): void {
    if (this.timer)       { clearInterval(this.timer); this.timer = null; }
    if (this.eventSource) { this.eventSource.close();  this.eventSource = null; }
  }

  /** Connect to /api/stream. EventSource auto-reconnects on transient
   *  failures, so we don't need our own retry logic. */
  private openStream(): void {
    if (this.eventSource) return;
    try {
      const es = new EventSource("/api/stream");
      es.addEventListener("update", () => { void this.refresh(); });
      es.addEventListener("error",  () => { /* browser auto-retries */ });
      this.eventSource = es;
    } catch {
      // EventSource unavailable for any reason — 15s poll alone is fine.
    }
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  async refresh(): Promise<void> {
    try {
      const [accounts, buyers, templates] = await Promise.all([
        api.listAccounts(),
        api.listBuyers(),
        api.listTemplates(),
      ]);
      this.state = { ...this.state, accounts, buyers, templates, loading: false, error: null, loadedAt: Date.now() };
    } catch (err) {
      this.state = {
        ...this.state,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      };
    }
    this.emit();
  }

  setSelectedAccountId(id: "all" | string): void {
    if (this.state.selectedAccountId === id) return;
    this.state = { ...this.state, selectedAccountId: id };
    this.emit();
  }

  setSearch(search: string): void {
    const filters = { ...this.state.filters, search };
    this.state = { ...this.state, filters };
    saveFilters(filters);
    this.emit();
  }

  toggleStatusFilter(s: ConversationStatus): void {
    const cur = this.state.filters.statuses;
    const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
    const filters = { ...this.state.filters, statuses: next };
    this.state = { ...this.state, filters };
    saveFilters(filters);
    this.emit();
  }

  setUnreadOnly(v: boolean): void {
    const filters = { ...this.state.filters, unreadOnly: v };
    this.state = { ...this.state, filters };
    saveFilters(filters);
    this.emit();
  }

  resetFilters(): void {
    const filters: PipelineFilters = { search: "", statuses: [], unreadOnly: false };
    this.state = { ...this.state, filters };
    saveFilters(filters);
    this.emit();
  }

  toggleConvSelected(convId: string): void {
    const next = new Set(this.state.selectedConvIds);
    if (next.has(convId)) next.delete(convId);
    else next.add(convId);
    this.state = { ...this.state, selectedConvIds: next };
    this.emit();
  }

  selectAllConvs(convIds: string[]): void {
    const cur  = this.state.selectedConvIds;
    const next = new Set(cur);
    // If everything visible is already selected, treat as "unselect all visible"
    const allAlreadySelected = convIds.every((id) => cur.has(id));
    if (allAlreadySelected) {
      for (const id of convIds) next.delete(id);
    } else {
      for (const id of convIds) next.add(id);
    }
    this.state = { ...this.state, selectedConvIds: next };
    this.emit();
  }

  clearSelection(): void {
    if (this.state.selectedConvIds.size === 0) return;
    this.state = { ...this.state, selectedConvIds: new Set() };
    this.emit();
  }

  /** Send the same text to every selected conversation. Throttles at
   *  ~1.5 sends/sec to stay under Resend's 2/sec free-tier limit. */
  async sendBulkReply(text: string, opts: { ids?: string[] } = {}): Promise<void> {
    // Caller can pass the exact id list (post-dedupe from BulkActionBar)
    // or omit ids to use the current selection unchanged.
    const ids = opts.ids ?? [...this.state.selectedConvIds];
    if (ids.length === 0 || !text.trim()) return;

    this.state = {
      ...this.state,
      bulkStatus: { sending: true, sent: 0, failed: 0, total: ids.length },
    };
    this.emit();

    let sent = 0, failed = 0;
    for (const id of ids) {
      try {
        // Each bulk send gets its own idempotency key so iOS-style
        // POST retries don't duplicate (FEST-PHASE-2.Z). Tied to
        // conv id + timestamp so the same throttled iteration retries
        // are deduped by the server.
        const idemKey = `bulk_${id}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        await api.postReply(id, text, idemKey);
        sent += 1;
      } catch {
        failed += 1;
      }
      this.state = {
        ...this.state,
        bulkStatus: { sending: true, sent, failed, total: ids.length },
      };
      this.emit();
      // Throttle: ~660ms between sends keeps us at ~1.5/sec.
      await new Promise<void>((r) => setTimeout(r, 660));
    }

    this.state = {
      ...this.state,
      bulkStatus:      { sending: false, sent, failed, total: ids.length },
      selectedConvIds: new Set(),
    };
    this.emit();
    // Refresh so message rows + last_activity_at update.
    void this.refresh();

    // Auto-clear the status banner after 6s
    setTimeout(() => {
      if (this.state.bulkStatus && !this.state.bulkStatus.sending) {
        this.state = { ...this.state, bulkStatus: null };
        this.emit();
      }
    }, 6000);
  }

  /** Bulk-clear unread state. Optimistically zeros all local
   *  conv_unread_count values, then fires the API. Reconciles on the
   *  next refresh if the call fails. */
  markAllRead(): void {
    const buyers = this.state.buyers.map((b) =>
      b.conv_unread_count > 0 ? { ...b, conv_unread_count: 0 } : b,
    );
    this.state = { ...this.state, buyers };
    this.emit();
    void api.markAllRead().catch(() => { /* re-syncs on next refresh */ });
  }

  openEditor(conversationId: string): void {
    this.state = { ...this.state, editingConversationId: conversationId };
    this.emit();
    // Best-effort mark-read on open. Server is the source of truth; we
    // optimistically zero the local unread count too so the badge clears
    // immediately without waiting for the next refresh.
    const buyers = this.state.buyers.map((b) =>
      b.conv_id === conversationId && b.conv_unread_count > 0
        ? { ...b, conv_unread_count: 0 }
        : b,
    );
    this.state = { ...this.state, buyers };
    this.emit();
    void api.markRead(conversationId).catch(() => { /* re-syncs on next refresh */ });
  }
  closeEditor(): void {
    if (this.state.editingConversationId === null) return;
    this.state = { ...this.state, editingConversationId: null };
    this.emit();
  }

  togglePreviewCollapsed(): void {
    const next = !this.state.previewCollapsed;
    this.state = { ...this.state, previewCollapsed: next };
    try { localStorage.setItem(PREVIEW_COLLAPSED_KEY, next ? "1" : "0"); } catch { /* full storage is fine */ }
    this.emit();
  }

  toggleNotificationsMuted(): void {
    const next = !this.state.notificationsMuted;
    this.state = { ...this.state, notificationsMuted: next };
    try { localStorage.setItem(NOTIF_MUTED_KEY, next ? "1" : "0"); } catch { /* */ }
    this.emit();
  }

  dismissNotificationPrompt(): void {
    this.state = { ...this.state, notificationPromptDismissed: true };
    try { localStorage.setItem(NOTIF_PROMPT_DISMISSED_KEY, "1"); } catch { /* */ }
    this.emit();
  }

  setNotificationPermission(perm: "default" | "granted" | "denied" | "unsupported"): void {
    if (this.state.notificationPermission === perm) return;
    this.state = { ...this.state, notificationPermission: perm };
    this.emit();
  }

  /** Local-only buyer patch (FEST-PHASE-2.U). Used by the editor's
   *  saveBuyerIfChanged so the next render sees the new phone/notes
   *  immediately, instead of waiting on the api.patchBuyer round-trip. */
  optimisticPatchBuyer(buyerId: string, patch: { phone?: string | null; notes?: string | null }): void {
    const buyers = this.state.buyers.map((b) =>
      b.id === buyerId
        ? {
            ...b,
            phone: patch.phone !== undefined ? patch.phone : b.phone,
            notes: patch.notes !== undefined ? patch.notes : b.notes,
          }
        : b,
    );
    this.state = { ...this.state, buyers };
    this.emit();
  }

  /** Soft archive (FEST-PHASE-2.P). Optimistic — flip the local
   *  conv_archived_at, drop selection, then PATCH in background. SSE
   *  will reconcile on the next broadcast if anything diverges. */
  async archiveConversation(convId: string): Promise<void> {
    const now = Date.now();
    const buyers = this.state.buyers.map((b) =>
      b.conv_id === convId ? { ...b, conv_archived_at: now } : b
    );
    const selected = new Set(this.state.selectedConvIds);
    selected.delete(convId);
    this.state = { ...this.state, buyers, selectedConvIds: selected };
    this.emit();
    try { await api.archiveConversation(convId); }
    catch { void this.refresh(); /* re-sync from server on error */ }
  }

  async unarchiveConversation(convId: string): Promise<void> {
    const buyers = this.state.buyers.map((b) =>
      b.conv_id === convId ? { ...b, conv_archived_at: null } : b
    );
    this.state = { ...this.state, buyers };
    this.emit();
    try { await api.unarchiveConversation(convId); }
    catch { void this.refresh(); }
  }

  /** Bulk archive — runs sequentially with the same throttle as
   *  bulk reply, so we never exceed the worker's PATCH rate. */
  async archiveSelected(): Promise<void> {
    const ids = [...this.state.selectedConvIds];
    if (ids.length === 0) return;
    for (const id of ids) {
      try { await api.archiveConversation(id); }
      catch { /* keep going; final refresh repairs partial state */ }
    }
    this.state = { ...this.state, selectedConvIds: new Set() };
    this.emit();
    void this.refresh();
  }

  /** Optimistic in-place update of the local buyers slice, then PATCH. */
  async patchEditingConversation(patch: PatchConversationBody): Promise<void> {
    const id = this.state.editingConversationId;
    if (!id) return;
    const buyers = this.state.buyers.map((b) => b.conv_id === id ? {
      ...b,
      conv_status:             patch.status            ?? b.conv_status,
      conv_tickets_count:      patch.tickets_count      !== undefined ? patch.tickets_count      : b.conv_tickets_count,
      conv_agreed_price_cents: patch.agreed_price_cents !== undefined ? patch.agreed_price_cents : b.conv_agreed_price_cents,
      conv_agreed_meet_spot:   patch.agreed_meet_spot   !== undefined ? patch.agreed_meet_spot   : b.conv_agreed_meet_spot,
      conv_agreed_meet_at:     patch.agreed_meet_at     !== undefined ? patch.agreed_meet_at     : b.conv_agreed_meet_at,
      conv_last_activity_at:   Date.now(),
    } : b);
    this.state = { ...this.state, buyers };
    this.emit();
    try {
      await api.patchConversation(id, patch);
    } catch (err) {
      // Best-effort: refresh to reconcile from server on failure.
      this.state = { ...this.state, error: err instanceof Error ? err.message : String(err) };
      this.emit();
      await this.refresh();
    }
  }

  private emit(): void { for (const fn of this.listeners) fn(); }
}

export const store = new Store();
